import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ContactOrAccountDetailsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {AsyncLoader} from '../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {buildContactsManagerAccountContactsUrl} from '../../../../helpers/contactsManagerHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {CardModel} from '../../../../models/api/cardModel';
import {AccountDetailsCardsState} from '../../../../store/panels/accounts/accountDetailsPanelState';
import {EmailDomainDropdownModesEnum} from '../accountInformation/emailDomainDropdown';
import {EmailDomainDropdownContainer} from '../accountInformation/emailDomainDropdownContainer';
import {AccountDetailsContact} from './accountDetailsContact';
import {AccountDetailsContactContainer} from './accountDetailsContactContainer';

/*
 * Props.
 */

interface AccountDetailsContactsProps {
  panelId: ContactOrAccountDetailsPanelId;
  account: AccountModel;
  cardsState: AccountDetailsCardsState;
  cards: ReadonlyArray<CardModel> | undefined;
  fetchCardsForAccountDetails: (panelId: ContactOrAccountDetailsPanelId, accountId: number) => void;
  closeSidePanel: () => void;
  navigateToLink: (arg: string) => void;
}

/*
 * Style.
 */

const StyledErrorDiv = styled.div`
  padding: 32px 0;
`;

const StyledCardsContainerDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 12px;

  padding-top: 7px;
`;

const StyledViewAllButton = styled(Button)`
  margin-left: -12px;
`;

const StyledEmptyStateDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin-top: 4px;
`;

const StyledAddADomainDiv = styled.div`
  margin-top: 8px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  display: inline;
`;

const StyledAddDomainButton = styled(InlineLinkButton)`
  font-weight: ${fontWeights.semibold};
`;

const StyledButtonContainerDiv = styled.div`
  display: flex;
`;

/*
 * Component.
 */

export const AccountDetailsContacts: FC<AccountDetailsContactsProps> = (props) => {
  const {panelId, account, cardsState, cards, fetchCardsForAccountDetails, closeSidePanel, navigateToLink} =
    props;

  if (cardsState.loadingStatus === AsyncStatusesEnum.LOADING) {
    return (
      <StyledCardsContainerDiv>
        <AccountDetailsContact isLoading />
        <AccountDetailsContact isLoading />
        <AccountDetailsContact isLoading />
      </StyledCardsContainerDiv>
    );
  }

  if (cardsState.loadingStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorDiv>
        <AsyncLoader
          size={VisualSizesEnum.LARGE}
          status={AsyncStatusesEnum.FAILED}
          error={<Trans id="account-details-error-loading-cards">Something went wrong.</Trans>}
          onRetryClick={() => {
            fetchCardsForAccountDetails(panelId, account.id);
          }}
        />
      </StyledErrorDiv>
    );
  }
  if (!cards || cards.length === 0) {
    return (
      <StyledEmptyStateDiv>
        <div>
          <Trans id="account-details-contacts-no-contacts">No contacts</Trans>
        </div>
        {account && account.domains.length === 0 && (
          <StyledAddADomainDiv>
            <StyledPopoverCoordinator
              renderButton={(isOpen) => (
                <StyledAddDomainButton hasHover={isOpen}>
                  <Trans id="account-details-contacts-add-a-domain">Add a domain</Trans>
                </StyledAddDomainButton>
              )}
              renderModal={(popoverProps) => (
                <EmailDomainDropdownContainer
                  panelId={panelId}
                  account={account}
                  mode={EmailDomainDropdownModesEnum.ADD}
                  popoverTransferProps={popoverProps}
                  onCancel={() => popoverProps.onRequestClose()}
                  onSave={() => {
                    popoverProps.onRequestClose();
                  }}
                />
              )}
            />{' '}
            <Trans id="account-details-contacts-add-domain-associate-message">
              to auto-associate contacts.
            </Trans>
          </StyledAddADomainDiv>
        )}
      </StyledEmptyStateDiv>
    );
  }

  return (
    <StyledCardsContainerDiv>
      {cards.map((card) => (
        <AccountDetailsContactContainer key={card.id} panelId={panelId} card={card} />
      ))}
      <StyledButtonContainerDiv>
        <StyledViewAllButton
          variant={ButtonVariantsEnum.LINK}
          onClick={() => {
            closeSidePanel();
            navigateToLink(buildContactsManagerAccountContactsUrl(account.id));
          }}
        >
          <Trans id="account-details-contacts-view-all">View all</Trans>
        </StyledViewAllButton>
      </StyledButtonContainerDiv>
    </StyledCardsContainerDiv>
  );
};
